import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { PRODUCTS } from "@/lib/products";
import MarketingHeader from "@/components/MarketingHeader";
import ProductMark from "@/components/ProductMark";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/1381722/pexels-photo-1381722.jpeg";

export default function Landing() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/40">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25 dark:opacity-30"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/60 to-background" />
        <div className="absolute inset-0 grid-bg opacity-30 stripe-mask" />

        <div className="relative mx-auto max-w-7xl px-5 pt-20 pb-28 sm:pt-28 sm:pb-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span
                data-testid="hero-eyebrow"
                className="inline-flex items-center gap-2 rounded-sm border border-border/60 bg-card/40 backdrop-blur px-3 py-1 text-xs uppercase tracking-[0.18em] text-muted-foreground"
              >
                <Sparkles className="h-3 w-3" />
                {t("hero.eyebrow")}
              </span>
              <h1
                data-testid="hero-title"
                className="mt-6 font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-foreground"
              >
                {t("hero.title_a")}
                <br />
                <span className="italic text-muted-foreground">{t("hero.title_b")}</span>
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: "linear-gradient(135deg, #E1306C, #F56040, #833AB4)" }}
                >
                  {t("hero.title_c")}
                </span>
              </h1>
              <p
                data-testid="hero-sub"
                className="mt-8 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed"
              >
                {t("hero.sub")}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <button
                  data-testid="hero-cta-primary"
                  onClick={() => nav("/auth")}
                  className="group inline-flex h-12 items-center gap-2 rounded-sm bg-foreground px-6 text-sm font-medium text-background hover:opacity-90 transition-opacity"
                >
                  {t("hero.cta_primary")}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 rtl:rotate-180" />
                </button>
                <a
                  href="#investors"
                  data-testid="hero-cta-secondary"
                  className="inline-flex h-12 items-center gap-2 rounded-sm border border-border bg-card/40 backdrop-blur px-6 text-sm font-medium hover:bg-card transition-colors"
                >
                  {t("hero.cta_secondary")}
                </a>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="grid grid-cols-3 lg:grid-cols-1 gap-4 lg:gap-6">
                {[
                  { v: t("hero.stat_one"), d: t("hero.stat_one_d") },
                  { v: t("hero.stat_two"), d: t("hero.stat_two_d") },
                  { v: t("hero.stat_three"), d: t("hero.stat_three_d") },
                ].map((s, i) => (
                  <div
                    key={i}
                    className="glass rounded-sm p-4 lg:p-5 animate-fade-up"
                    style={{ animationDelay: `${0.2 + i * 0.08}s` }}
                  >
                    <div className="font-serif text-3xl lg:text-4xl tracking-tight">{s.v}</div>
                    <div className="mt-1 text-xs lg:text-sm text-muted-foreground leading-snug">{s.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="relative border-b border-border/40 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-14">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("products.eyebrow")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">{t("products.title")}</h2>
            </div>
            <p className="lg:col-span-7 lg:col-start-6 text-muted-foreground leading-relaxed text-lg">
              {t("products.sub")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRODUCTS.map((p, i) => (
              <Link
                key={p.key}
                to="/auth"
                data-testid={`product-card-${p.key}`}
                className="group relative overflow-hidden rounded-sm border border-border/60 bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-transparent"
                style={{ animationDelay: `${i * 0.05}s` }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 0 0 1px ${p.accent}, 0 30px 80px -25px ${p.accent}55`)}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "")}
              >
                <div className="flex items-start justify-between">
                  <ProductMark product={p} size={48} />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    vs {p.disrupt}
                  </span>
                </div>
                <h3 className="mt-7 font-display text-2xl tracking-tight">{p.name}</h3>
                <p className="mt-1 text-sm" style={{ color: p.accent }}>
                  {p.tagline[lang] || p.tagline.en}
                </p>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                  {p.blurb[lang] || p.blurb.en}
                </p>
                <div className="mt-7 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: p.accent }}>
                  {t("products.open")}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY NOW */}
      <section className="relative border-b border-border/40 py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 arabic-pattern opacity-50" />
        <div className="relative mx-auto max-w-7xl px-5 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("why.eyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">{t("why.title")}</h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-lg leading-relaxed text-muted-foreground">
            <p>{t("why.p1")}</p>
            <p className="text-foreground">{t("why.p2")}</p>
          </div>
        </div>
      </section>

      {/* MARKET */}
      <section className="relative border-b border-border/40 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("market.eyebrow")}</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-3xl">{t("market.title")}</h2>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-border/60 border border-border/60">
            {t("market.stats").map((s, i) => (
              <div key={i} className="bg-background p-6 lg:p-8" data-testid={`market-stat-${i}`}>
                <div className="font-serif text-3xl sm:text-4xl tracking-tight">{s.v}</div>
                <div className="mt-2 text-sm text-muted-foreground">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-border/40 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("nav.pricing")}</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight max-w-3xl">
            Built so every business can afford the tools they were locked out of.
          </h2>

          {pricing && (
            <div className="mt-14 space-y-12">
              {PRODUCTS.map((p) => (
                <div key={p.key} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-3 flex items-start gap-3">
                    <ProductMark product={p} size={40} />
                    <div>
                      <div className="font-display text-xl">{p.name}</div>
                      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">
                        vs {p.disrupt}
                      </div>
                    </div>
                  </div>
                  <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {(pricing[p.key] || []).map((tier, i) => (
                      <button
                        key={i}
                        data-testid={`landing-pricing-${p.key}-${i}`}
                        onClick={() => nav("/auth")}
                        className={`text-start rounded-sm border p-5 transition-colors hover:-translate-y-0.5 ${
                          tier.highlight
                            ? "border-foreground bg-card"
                            : "border-border/60 bg-card/50 hover:border-border"
                        }`}
                      >
                        <div className="text-sm font-medium">{tier.tier}</div>
                        <div className="mt-2 font-serif text-3xl tracking-tight">
                          {tier.price === 0 ? "Free" : `$${tier.price.toLocaleString()}`}
                          {tier.price !== 0 && (
                            <span className="text-sm text-muted-foreground font-sans">
                              /{tier.annual ? "yr" : "mo"}
                            </span>
                          )}
                        </div>
                        {tier.assets && (
                          <div className="mt-2 text-xs text-muted-foreground">{tier.assets}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* INVESTORS */}
      <section id="investors" className="border-b border-border/40 py-24 sm:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative mx-auto max-w-7xl px-5 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("investors.eyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]">{t("investors.title")}</h2>
            <a
              href="mailto:invest@sanaa.ai"
              data-testid="investor-cta"
              className="mt-8 inline-flex h-12 items-center gap-2 rounded-sm bg-foreground px-6 text-sm font-medium text-background hover:opacity-90"
            >
              {t("investors.cta")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </a>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="space-y-4">
              {t("investors.points").map((pt, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 border-b border-border/40 pb-4 last:border-0"
                >
                  <Check className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "#E1306C" }} />
                  <span className="text-lg text-foreground">{pt}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-5 text-center">
          <h2 className="font-serif text-5xl sm:text-7xl tracking-tight leading-[1]">{t("cta.title")}</h2>
          <p className="mt-6 text-lg text-muted-foreground">{t("cta.sub")}</p>
          <button
            data-testid="footer-cta"
            onClick={() => nav("/auth")}
            className="mt-10 inline-flex h-12 items-center gap-2 rounded-sm bg-foreground px-7 text-sm font-medium text-background hover:opacity-90"
          >
            {t("cta.btn")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border/60 py-10">
        <div className="mx-auto max-w-7xl px-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 text-foreground">
            <div className="h-5 w-5 rounded-sm" style={{ background: "linear-gradient(135deg, #E1306C, #F56040, #833AB4)" }} />
            <span className="font-display">Sanaa AI</span>
            <span className="text-muted-foreground">— Dubai · Lagos · Nairobi</span>
          </div>
          <div>© {new Date().getFullYear()} Sanaa AI FZ-LLC. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
